import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, CheckCircle2 } from "lucide-react";

export default function Picking() {
  const [picks, setPicks] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse_id: "", order_ref: "", customer_name: "", lines: [] });
  const [completeOpen, setCompleteOpen] = useState(null);
  const [completeForm, setCompleteForm] = useState([]);

  const load = async () => {
    const [p, it, wh] = await Promise.all([
      api.get("/warehouse/picks"),
      api.get("/inventory/items"),
      api.get("/inventory/warehouses"),
    ]);
    setPicks(p.data); setItems(it.data); setWarehouses(wh.data);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", qty_required: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  const save = async () => {
    if (!form.warehouse_id || form.lines.length === 0) { toast.error("Pick warehouse + at least one line"); return; }
    try {
      await api.post("/warehouse/picks", form);
      toast.success("Pick list created");
      setOpen(false); setForm({ warehouse_id: "", order_ref: "", customer_name: "", lines: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openComplete = (pick) => {
    setCompleteOpen(pick);
    setCompleteForm(pick.lines.map((l) => ({ item_id: l.item_id, item_name: l.item_name, qty_required: l.qty_required, picked_qty: l.qty_required })));
  };

  const submitComplete = async () => {
    try {
      await api.post(`/warehouse/picks/${completeOpen.id}/complete`, { lines: completeForm });
      toast.success("Pick completed and stock posted");
      setCompleteOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-picking">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-primary" /> Picking
          </h1>
          <p className="text-muted-foreground">Generate pick lists, complete them, auto-post OUT stock movements.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pick-new"><Plus className="w-4 h-4 mr-1" /> New pick</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>Order ref</TableHead><TableHead>Customer</TableHead>
            <TableHead>Warehouse</TableHead><TableHead className="text-right">Lines</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {picks.map((p) => (
              <TableRow key={p.id} data-testid={`pick-row-${p.number}`}>
                <TableCell className="font-mono text-xs">{p.number}</TableCell>
                <TableCell className="font-mono text-xs">{p.order_ref || "—"}</TableCell>
                <TableCell>{p.customer_name || "—"}</TableCell>
                <TableCell>{p.warehouse_name}</TableCell>
                <TableCell className="text-right">{p.lines?.length ?? 0}</TableCell>
                <TableCell><Badge variant={p.status === "COMPLETED" ? "default" : "outline"}>{p.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {p.status !== "COMPLETED" && (
                    <Button size="sm" variant="outline" onClick={() => openComplete(p)} data-testid={`pick-complete-${p.number}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {picks.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No picks yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* New pick dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New pick list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                  <SelectTrigger data-testid="pick-wh"><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Order ref</Label>
                <Input value={form.order_ref} onChange={(e) => setForm({ ...form, order_ref: e.target.value })} data-testid="pick-orderref" placeholder="e.g. SO-2026-00012" />
              </div>
              <div>
                <Label>Customer</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="pick-customer" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Label>Lines</Label>
              <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-4 h-4 mr-1" /> Add line</Button>
            </div>
            {form.lines.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-8">
                  <Label className="text-[10px]">Item</Label>
                  <Select value={ln.item_id} onValueChange={(v) => updateLine(i, { item_id: v })}>
                    <SelectTrigger data-testid={`pick-line-item-${i}`}><SelectValue placeholder="Item…" /></SelectTrigger>
                    <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px]">Qty required</Label>
                  <Input type="number" value={ln.qty_required} onChange={(e) => updateLine(i, { qty_required: parseFloat(e.target.value || "0") })} data-testid={`pick-line-qty-${i}`} />
                </div>
                <div className="col-span-1">
                  <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            ))}
            {form.lines.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No lines yet.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="pick-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete pick dialog */}
      <Dialog open={!!completeOpen} onOpenChange={(v) => !v && setCompleteOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Complete pick · {completeOpen?.number}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Enter actual picked quantity per line. Stock will post OUT on submit.</p>
            {completeForm.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-8 text-sm">{l.item_name}<span className="text-muted-foreground text-xs ml-1">(req {l.qty_required})</span></div>
                <Input type="number" className="col-span-4" value={l.picked_qty} onChange={(e) => setCompleteForm(completeForm.map((x, idx) => idx === i ? { ...x, picked_qty: parseFloat(e.target.value || "0") } : x))} data-testid={`pick-complete-qty-${i}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(null)}>Cancel</Button>
            <Button onClick={submitComplete} data-testid="pick-complete-submit">Post stock & complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
